import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Home, Briefcase, MapPin, Plus, Trash2, LocateFixed, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { LocationPicker } from "@/components/customer/LocationPicker";

const EMPTY = { label: "Home", line1: "", line2: "", city: "Hyderabad", pincode: "", instructions: "", lat: null, lng: null };

export default function Addresses() {
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [adding, setAdding] = useState(false);
  const [locating, setLocating] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/addresses").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const useCurrentLocation = () => {
    if (!("geolocation" in navigator)) {
      return toast.error("Your browser doesn't support location. Please enter your address manually.");
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        try {
          const { data } = await api.get(`/geo/reverse?lat=${lat}&lng=${lng}`);
          setForm((f) => ({
            ...f,
            line1: data.line1 || f.line1,
            line2: data.area || f.line2,
            city: data.city || f.city,
            pincode: data.pincode || f.pincode,
            lat, lng,
          }));
          toast.success("Location found — please review and edit if needed");
        } catch (e) {
          setForm((f) => ({ ...f, lat, lng }));
          toast.error(apiError(e) || "Couldn't fetch address details. Please fill it in manually.");
        } finally { setLocating(false); }
      },
      (err) => {
        setLocating(false);
        const msg = err.code === 1
          ? "We couldn't access your location. Please allow location permission or enter your address manually."
          : "Location unavailable right now. Please enter your address manually.";
        toast.error(msg);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  const add = async () => {
    if (!form.line1) return toast.error("Enter address line");
    try {
      await api.post("/addresses", form);
      toast.success("Address saved");
      setAdding(false); setForm(EMPTY); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { await api.delete(`/addresses/${id}`); toast.success("Address removed"); load(); };

  return (
    <div className="min-h-screen bg-surface max-w-[520px] mx-auto pb-10">
      <div className="sticky top-0 z-40 bg-white border-b border-border px-3 py-3 flex items-center gap-2">
        <button onClick={() => nav(-1)} className="w-9 h-9 flex items-center justify-center tap"><ArrowLeft size={22} /></button>
        <h1 className="font-display font-bold text-lg">Manage Addresses</h1>
      </div>
      <div className="px-4 py-4 space-y-3">
        {list.map((a) => (
          <div key={a.id} className="bg-white rounded-2xl border border-border p-4 flex items-start gap-3" data-testid={`addr-${a.id}`}>
            <div className="w-10 h-10 rounded-xl bg-coral-light flex items-center justify-center shrink-0">{a.label === "Work" ? <Briefcase size={18} className="text-coral" /> : a.label === "Home" ? <Home size={18} className="text-coral" /> : <MapPin size={18} className="text-coral" />}</div>
            <div className="flex-1 min-w-0"><div className="font-bold text-sm flex items-center gap-1.5">{a.label}{a.lat && <MapPin size={12} className="text-veg" title="Pinned location" />}</div><div className="text-xs text-ink-600">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city} {a.pincode}</div>{a.instructions && <div className="text-[11px] text-ink-600 italic mt-0.5">{a.instructions}</div>}</div>
            <button onClick={() => del(a.id)} className="text-ink-600 tap" data-testid={`del-addr-${a.id}`}><Trash2 size={17} /></button>
          </div>
        ))}
        {adding ? (
          <div className="bg-white rounded-2xl border border-border p-4 space-y-2.5">
            <button
              onClick={useCurrentLocation}
              disabled={locating}
              className="w-full bg-coral-light border border-coral/30 text-coral font-bold py-3 rounded-xl flex items-center justify-center gap-2 tap disabled:opacity-70"
              data-testid="use-current-location"
            >
              {locating ? <Loader2 size={17} className="animate-spin" /> : <LocateFixed size={17} />}
              {locating ? "Getting your location…" : "Use Current Location"}
            </button>

            {form.lat && (
              <div className="space-y-1.5">
                <LocationPicker lat={form.lat} lng={form.lng} onChange={({ lat, lng }) => setForm((f) => ({ ...f, lat, lng }))} />
                <div className="text-[11px] text-ink-600 text-center">Drag the pin or tap the map to fine-tune your exact location</div>
              </div>
            )}

            <div className="flex gap-2">{["Home", "Work", "Other"].map((l) => <button key={l} onClick={() => setForm((f) => ({ ...f, label: l }))} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${form.label === l ? "bg-coral text-white border-coral" : "bg-white border-border"}`}>{l}</button>)}</div>
            <input value={form.line1} onChange={(e) => setForm((f) => ({ ...f, line1: e.target.value }))} placeholder="Flat / House no, Building" className="w-full bg-surface rounded-lg px-3 py-2.5 text-sm outline-none" data-testid="addr-line1" />
            <input value={form.line2} onChange={(e) => setForm((f) => ({ ...f, line2: e.target.value }))} placeholder="Landmark / Area" className="w-full bg-surface rounded-lg px-3 py-2.5 text-sm outline-none" data-testid="addr-line2" />
            <div className="flex gap-2"><input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} placeholder="City" className="flex-1 bg-surface rounded-lg px-3 py-2.5 text-sm outline-none" data-testid="addr-city" /><input value={form.pincode} onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value }))} placeholder="Pincode" className="w-28 bg-surface rounded-lg px-3 py-2.5 text-sm outline-none" data-testid="addr-pincode" /></div>
            <input value={form.instructions} onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value }))} placeholder="Delivery instructions" className="w-full bg-surface rounded-lg px-3 py-2.5 text-sm outline-none" />
            <div className="flex gap-2"><button onClick={add} className="flex-1 bg-coral text-white font-bold py-2.5 rounded-lg text-sm tap" data-testid="save-new-addr">{form.lat ? "Confirm & Save" : "Save address"}</button><button onClick={() => { setAdding(false); setForm(EMPTY); }} className="px-4 bg-white border border-border rounded-lg text-sm">Cancel</button></div>
          </div>
        ) : (
          <button onClick={() => setAdding(true)} className="w-full border-2 border-dashed border-coral text-coral font-bold py-3 rounded-2xl flex items-center justify-center gap-2 tap" data-testid="add-address"><Plus size={18} /> Add new address</button>
        )}
      </div>
    </div>
  );
}
